import { parse as urlParse } from 'url';

import GhostContentAPI, { Authors, Params, Tag } from '@tryghost/content-api';

import ResearchApiConfig from '../../../ResearchApiConfig';
import {
    createNextFeatureImages,
    createNextImage,
    createNextProfileImages,
    createNextProfileImagesFromPosts,
} from './ghost-images';
import { GhostAuthor, GhostPostOrPage, GhostPostsOrPages, GhostSettings, GhostTags } from './ghost-interfaces';
import { normalizePost } from './ghost-normalize';

export const api = new GhostContentAPI({
    url: ResearchApiConfig.ghostAPIUrl,
    key: ResearchApiConfig.ghostAPIKey,
    version: 'v5.0',
});

export const postAndPageFetchOptions: Params = {
    limit: 'all',
    include: ['tags', 'authors', 'count.posts'],
    order: ['featured DESC', 'published_at DESC'],
};

const tagAndAuthorFetchOptions: Params = {
    limit: 'all',
    include: 'count.posts',
};

const postAndPageSlugOptions: Params = {
    limit: 'all',
    fields: 'slug',
};

const excludePostOrPageBySlug = () => {
    return 'slug:-contact';
};

interface ToStringTagSlugsAndSupportedAssetNames {
    tagSlugs?: string[];
    supportedAssetNames?: string[];
}

export const toStringTagSlugsAndSupportedAssetNames = (props: ToStringTagSlugsAndSupportedAssetNames) =>
    `${
        props?.tagSlugs?.length
            ? `${props?.tagSlugs?.join(',')}${
                  props?.supportedAssetNames?.length ? `,${props?.supportedAssetNames?.join(',')}` : ''
              }`
            : props?.supportedAssetNames?.join(',')
    }` || '';

// Api methods
export async function getAllSettings(): Promise<GhostSettings> {
    // const cached = getCache<SettingsResponse>('settings')
    // if (cached) return cached
    const settings = await api.settings.browse();
    settings.url = settings?.url?.replace(/\/$/, ``) || ResearchApiConfig.siteUrl;

    const iconImage = await createNextImage(settings.icon);
    const logoImage = await createNextImage(settings.logo);
    const coverImage = await createNextImage(settings.cover_image);

    // setCache('settings', result)

    return {
        ...settings,
        ...(iconImage && { iconImage }),
        ...(logoImage && { logoImage }),
        ...(coverImage && { coverImage }),
    };
}

export async function getAllTags(): Promise<GhostTags> {
    const tags = await api.tags.browse({ ...tagAndAuthorFetchOptions, filter: `visibility:public` });
    const images = await createNextFeatureImages(tags);
    return images;
}

export async function getAllAuthors(): Promise<Authors> {
    const authors = await api.authors.browse(tagAndAuthorFetchOptions);
    return createNextProfileImages(authors);
}

export async function getAllPosts(props?: {
    limit: number;
    order?: string;
    tagSlugs?: string[];
    supportedAssetNames?: string[];
    authorSlug?: string;
    page?: number;
}): Promise<GhostPostsOrPages> {
    const posts = await api.posts.browse({
        ...postAndPageFetchOptions,
        filter: `${excludePostOrPageBySlug()}${
            ((props?.tagSlugs && props?.tagSlugs.length > 0) ||
                (props?.supportedAssetNames && props?.supportedAssetNames.length > 0)) &&
            `+tags.slug:[${toStringTagSlugsAndSupportedAssetNames({
                tagSlugs: props?.tagSlugs,
                supportedAssetNames: props?.supportedAssetNames,
            })}]`
        }${props?.authorSlug ? `+authors.slug:${props.authorSlug}` : ''}`,
        ...(props && { ...props }),
    });
    const results = await createNextProfileImagesFromPosts(posts);
    const images = await createNextFeatureImages(results);
    return images;
}

export async function getAllPostSlugs(): Promise<string[]> {
    const posts = await api.posts.browse(postAndPageSlugOptions);
    return posts.map((p: any) => p.slug);
}

export async function getAllPages(props?: { limit: number }): Promise<GhostPostsOrPages> {
    const pages = await api.pages.browse({
        ...postAndPageFetchOptions,
        filter: excludePostOrPageBySlug(),
        ...(props && { ...props }),
    });
    const images = await createNextFeatureImages(pages);
    return images;
}

// specific data by slug
export async function getTagBySlug(slug: string): Promise<Tag> {
    return api.tags.read({
        ...tagAndAuthorFetchOptions,
        slug,
    });
}

export async function getAuthorBySlug(slug: string): Promise<GhostAuthor> {
    const author = await api.authors.read({
        ...tagAndAuthorFetchOptions,
        slug,
    });

    const profileImage = await createNextImage(author.profile_image);

    return {
        ...author,
        ...(profileImage && { profileImage }),
    };
}

export async function getPostBySlug(slug: string): Promise<GhostPostOrPage | null> {
    let result: GhostPostOrPage;
    try {
        const post = await api.posts.read({
            ...postAndPageFetchOptions,
            slug,
        });

        // older Ghost versions do not throw error on 404
        if (!post) return null;

        const { url } = await getAllSettings();
        result = await normalizePost(post, (url && urlParse(url)) || undefined);
    } catch (e) {
        const error = e as { response?: { status: number } };
        if (error.response?.status === 404) return null;
        throw e;
    }
    return result;
}

export async function getPageBySlug(slug: string): Promise<GhostPostOrPage | null> {
    let result: GhostPostOrPage;
    try {
        const page = await api.pages.read({
            ...postAndPageFetchOptions,
            slug,
        });

        // older Ghost versions do not throw error on 404
        if (!page) return null;

        const { url } = await getAllSettings();
        result = await normalizePost(page, (url && urlParse(url)) || undefined);
    } catch (e) {
        const error = e as { response?: { status: number } };
        if (error.response?.status === 404) return null;
        throw e;
    }
    return result;
}

// specific data by author/tag slug
export async function getPostsByAuthor(slug: string): Promise<GhostPostsOrPages> {
    const posts = await api.posts.browse({
        ...postAndPageFetchOptions,
        filter: `authors.slug:${slug}`,
    });
    const images = await createNextFeatureImages(posts);
    return images;
}

export async function getPostsByTag(slug: string, limit?: number, excludeId?: string): Promise<GhostPostsOrPages> {
    const exclude = (excludeId && `+id:-${excludeId}`) || ``;
    const posts = await api.posts.browse({
        ...postAndPageFetchOptions,
        ...(limit && { limit: `${limit}` }),
        filter: `tags.slug:${slug}${exclude}`,
    });
    const images = await createNextFeatureImages(posts);
    return images;
}

export async function getPostsByTags(slugs: string[], limit?: number, excludeId?: string): Promise<GhostPostsOrPages> {
    const exclude = (excludeId && `+id:-${excludeId}`) || ``;
    const posts = await api.posts.browse({
        ...postAndPageFetchOptions,
        ...(limit && { limit: `${limit}` }),
        filter: `tags.slug:[${slugs.join(',')}]${exclude}`,
    });
    const images = await createNextFeatureImages(posts);
    return images;
}
